import { Controller } from '@hotwired/stimulus';

// The panel's client half: stamp the page url onto each question and keep the
// list scrolled to the newest bubble (replies arrive later, over the cable).
// Opening and closing is the native popover's job — `popovertarget` on the
// bar button (signed in) or the corner bubble (public), Esc and click-outside
// for free. A browser without popover gets the same by hand (shim).
// A bubble host's panel has two shapes (walk/CHAT.md § 6, § 7): the quick chat
// (`card`) and the AI drawer (`drawer`), `data-shape` on the panel, remembered
// per browser. Its quick links are modes (`mode`) or an action (`chip`).
// Registered as "help" by registerJane (register.js).
const SHAPE = 'fire-help-shape';

export default class extends Controller {
  static targets = [
    'panel',
    'messages',
    'form',
    'input',
    'url',
    'bubble',
    'card',
    'oops',
    'consent',
    'expand',
    'row',
    'exit',
    'mode',
    'tab',
  ];

  connect() {
    this.resize = () => this.open && this.place();
    addEventListener('resize', this.resize);
    // A card has no panel to open: the first focus inside it is the reach.
    this.reach = () => this.listen();
    this.element.addEventListener('focusin', this.reach, { once: true });
    if (this.hasPanelTarget && !('popover' in HTMLElement.prototype)) this.shim();
    if (this.hasBubbleTarget) {
      // Back restores a snapshot cached while open: the popover is shut again.
      this.bubbleTarget.setAttribute('aria-expanded', 'false');
      this.shape(this.recall());
    }
    this.show('prof');
  }

  // The talk is `data-turbo-permanent`: on a visit Turbo holds a placeholder in
  // its place while the new body connects, so `connect` cannot read it. It is
  // watched when it arrives (and again if a different one ever does).
  messagesTargetConnected(el) {
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.scroll());
    this.observer.observe(el, { childList: true });
  }

  disconnect() {
    this.observer?.disconnect();
    this.element.removeEventListener('focusin', this.reach);
    removeEventListener('resize', this.resize);
    document.removeEventListener('click', this.shimClick);
    document.removeEventListener('keydown', this.shimKey);
  }

  // Chrome before 114 has no popover, and 109 is the last Chrome Windows 7/8
  // get. There the panel renders open over the page, and no button can shut
  // it (APAE Arujá's /scan, 19/10). Hide it and drive it by hand: the same
  // three methods, the same `toggle` event, Esc and click-outside.
  shim() {
    const panel = this.panelTarget;
    const set = (open) => {
      if (open === !panel.hidden) return;
      panel.hidden = !open;
      const event = new Event('toggle');
      event.newState = open ? 'open' : 'closed';
      panel.dispatchEvent(event);
    };
    panel.hidden = true;
    panel.style.zIndex = '1000';
    panel.showPopover = () => set(true);
    panel.hidePopover = () => set(false);
    panel.togglePopover = () => set(panel.hidden);
    this.shimClick = (e) => {
      const button = e.target.closest?.('[popovertarget="fire-help"]');
      if (button) set(button.getAttribute('popovertargetaction') === 'hide' ? false : panel.hidden);
      else if (!panel.contains(e.target)) set(false);
    };
    this.shimKey = (e) => e.key === 'Escape' && set(false);
    document.addEventListener('click', this.shimClick);
    document.addEventListener('keydown', this.shimKey);
  }

  // The popover's own `toggle` event, both directions — only the opening one
  // has anything to do.
  opened(event) {
    this.open = event.newState === 'open';
    if (this.hasBubbleTarget) this.bubbleTarget.setAttribute('aria-expanded', this.open);
    if (!this.open) return this.restore();

    this.listen();
    this.place();
    this.urlTarget.value = window.location.href;
    this.scroll();
    // A touch screen's keyboard would open over the quick links: she taps the field.
    if (!matchMedia('(pointer: coarse)').matches) this.inputTarget.focus();
  }

  // The links' ✕ sits on top of the smart btn, so that is where focus was:
  // shut, it goes back to the smart btn and not to the top of the page.
  restore() {
    const at = document.activeElement;
    if (this.hasBubbleTarget && (at === document.body || this.panelTarget.contains(at))) this.bubbleTarget.focus();
  }

  // A page's morph refresh (a minute, kio's) must not touch the open panel:
  // its shape, the words half-typed and the scroll are not the server's. The
  // counts on the quick links are, and they live inside it: copied from the new render.
  keep(event) {
    event.preventDefault();
    const fresh = event.detail?.newElement?.querySelectorAll('[data-help-count]') ?? [];
    this.panelTarget.querySelectorAll('[data-help-count]').forEach((el, i) => {
      if (!fresh[i]) return;
      el.textContent = fresh[i].textContent;
      el.hidden = fresh[i].hidden;
    });
  }

  // The smart btn is the page's and morphs with it, except what is not the server's:
  // the popover's aria-expanded, nudge's lift (inline style), fab's fade (data-covers).
  hold(event) {
    const { attributeName: name } = event.detail;
    if ((name === 'aria-expanded' && this.open) || name === 'style' || name === 'data-covers') event.preventDefault();
  }

  // After a morph the smart btn may stand elsewhere (the app bar came or went): the ✕ follows.
  // A frame later, so a nudge that connected with the morph has lifted it.
  morphed() {
    requestAnimationFrame(() => this.open && this.place());
  }

  // ⤢ / ⤡: the other shape, remembered.
  expand() {
    const next = this.panelTarget.dataset.shape === 'drawer' ? 'card' : 'drawer';
    try {
      localStorage.setItem(SHAPE, next);
    } catch {} // a private window keeps nothing: the shape lasts the page
    this.shape(next);
  }

  recall() {
    try {
      return localStorage.getItem(SHAPE) === 'drawer' ? 'drawer' : 'card';
    } catch {
      return 'card';
    }
  }

  // The shape is `data-shape` on the panel (its descendants' variants read it),
  // and the ⤢ says which way it goes next.
  shape(name) {
    this.panelTarget.dataset.shape = name;
    if (this.hasExpandTarget) {
      const drawer = name === 'drawer';
      const label = this.expandTarget.dataset[drawer ? 'collapse' : 'expand'];
      this.expandTarget.setAttribute('aria-label', label);
      this.expandTarget.title = label;
    }
    if (this.open) this.place();
  }

  // A quick link that is a mode: show its room (the host's, or Prof's talk).
  mode(event) {
    this.show(event.params.mode);
  }

  show(name) {
    this.modeTargets.forEach((el) => (el.hidden = el.dataset.mode !== name));
    this.tabTargets.forEach((el) => el.setAttribute('aria-pressed', el.dataset.helpModeParam === name));
  }

  // A starter question, or a quick link that is the start of one (`send` false):
  // Prof's talk shows, the words go in the composer, and the same form sends
  // them or waits for her, caret at the end. Her words are hers: a link goes in
  // front of them (once), a starter over them sends nothing and gives her the field.
  chip({ params: { text, send = true } }) {
    if (this.formTarget.hidden) return; // the native app's yes is still to come
    this.show('prof');
    const input = this.inputTarget;
    if (!send || input.value.trim()) {
      if (!send && !input.value.startsWith(text)) input.value = text + input.value;
      input.focus();
      return input.setSelectionRange(input.value.length, input.value.length);
    }

    input.value = text;
    // The send button is the submitter: Turbo holds it disabled while the post is out, so a double press asks once.
    const submit = this.formTarget.querySelector('[type=submit]');
    return submit.disabled || this.formTarget.requestSubmit(submit);
  }

  // The first reach for help: only now does the page's empty ear frame load
  // the cable subscription (Jane::Help#ear). Once, from the panel's first open or a card's first focus.
  listen() {
    const ear = document.querySelector('[id^="fire-help-ear"]'); // keyed by user (help_dom_id)
    if (ear && !ear.getAttribute('src')) ear.setAttribute('src', ear.dataset.url);
  }

  // The app's one yes landed (jane/help/consent): the line goes, the composer
  // the server kept hidden opens.
  agreed(event) {
    if (!event.detail.success) return;

    this.consentTarget.remove();
    this.formTarget.hidden = false;
    this.inputTarget.focus();
  }

  // "Not now": the AI stays off. The panel shuts; a card, which has no panel,
  // drops its line.
  declined() {
    if (this.hasPanelTarget) this.panelTarget.hidePopover();
    else this.consentTarget.hidden = true;
  }

  // A 5xx is not Turbo's to paint over the whole page: the panel says it.
  refused(event) {
    if (event.detail.fetchResponse.serverError) event.preventDefault();
  }

  // Not sent: a 5xx or no network gets a line, the words stay in the field.
  // A 422 (a blank question) is refused quietly. Sent: only what went is cleared;
  // what she typed while the post was out stays, a link's prefix in front of it too.
  sent(event) {
    const { success, fetchResponse, formSubmission } = event.detail;
    if (!success) {
      if (this.hasOopsTarget && (!fetchResponse || fetchResponse.serverError))
        this.messagesTarget.append(this.oopsTarget.content.cloneNode(true));
      return;
    }

    this.inputTarget.value = this.inputTarget.value
      .replace(formSubmission.fetchRequest.body.get('text'), '')
      .trimStart();
    this.urlTarget.value = window.location.href;
    this.inputTarget.focus();
  }

  // The quick chat hangs off the smart btn, wherever the host parked it
  // (Jane.help_at): above it, on its side of the screen; on a phone it takes
  // the width, as far in as the smart btn is. The links' ✕ takes the smart
  // btn's size and corner, so it sits exactly over it. The drawer takes the
  // right edge, full height, and its row the foot of the sheet, padded to the
  // same corner. No smart btn (the bar's sheet) — the inline style already said where.
  place() {
    if (!this.hasBubbleTarget) return;

    const r = this.bubbleTarget.getBoundingClientRect();
    const d = document.documentElement;
    const [vw, vh] = [d.clientWidth, d.clientHeight]; // not innerWidth: a classic scrollbar is no part of the page
    const [s, row] = [this.panelTarget.style, this.rowTarget.style];
    // The layout size, not the painted one: a transform on the smart btn must not size the ✕
    // (offsetWidth would round 52.5 to 53, so it is the computed width).
    const { width, height } = getComputedStyle(this.bubbleTarget);
    Object.assign(this.exitTarget.style, { width, height });
    // A [popover] is fit-content and WebKit sizes a child's 100% under it to 0:
    // the wrapper has a height only once the panel does (the drawer).
    const wrap = this.rowTarget.parentElement;
    if (this.panelTarget.dataset.shape === 'drawer') {
      wrap.style.height = '100%';
      this.cardTarget.style.maxHeight = '';
      Object.assign(s, {
        top: '0',
        bottom: 'auto',
        left: 'auto',
        right: '0',
        height: '100dvh',
        width: 'min(92vw, max(26rem, 40vw))',
      });
      Object.assign(row, {
        alignSelf: 'stretch',
        paddingRight: `${vw - r.right}px`,
        paddingBottom: `${vh - r.bottom}px`,
      });
      return;
    }

    // Never taller than the room above the smart btn (a short phone, 200% zoom):
    // the talk gives way, the head and the composer stay. 128px is the floor, a head and a composer.
    wrap.style.height = '';
    const gap = parseFloat(getComputedStyle(wrap).rowGap) || 0;
    this.cardTarget.style.maxHeight = `${Math.max(r.bottom - this.rowTarget.offsetHeight - gap - 12, 128)}px`;

    const right = r.left + r.width / 2 > vw / 2;
    const side = right ? vw - r.right : r.left;
    Object.assign(s, {
      top: 'auto',
      bottom: `${vh - r.bottom}px`,
      height: '',
      width: vw < 640 ? `${vw - 2 * side}px` : '23rem',
    });
    Object.assign(s, right ? { left: 'auto', right: `${side}px` } : { left: `${side}px`, right: 'auto' });
    Object.assign(row, {
      alignSelf: right ? 'flex-end' : 'flex-start',
      paddingRight: '',
      paddingBottom: '',
    });
  }

  scroll() {
    if (this.hasMessagesTarget) this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }
}
